<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MemberFormDialog from './MemberFormDialog.vue';
import MemberGroupsPanel from './MemberGroupsPanel.vue';

const props = defineProps({
  appId: { type: [String, Number], required: true },
});

// Parent (AppEditor) keeps its own memberGroups for the BlockInspector + a count
// badge, so bubble both up whenever they change here.
const emit = defineEmits(['groupsChanged', 'countChanged']);

const store = useStore();
const { t } = useI18n();

const members = ref([]);
const groups = ref([]);
const isLoading = ref(false);
const search = ref('');
const groupFilter = ref('');
const selectedIds = ref([]);

const formDialogRef = ref(null);
const groupsPanelRef = ref(null);

// Client-side filtering keeps the table responsive without extra round-trips;
// member lists per app are small. (The API also supports ?q=/?group= server-side.)
const filteredMembers = computed(() => {
  const q = search.value.trim().toLowerCase();
  return members.value.filter(m => {
    const matchesQuery =
      !q ||
      m.email.toLowerCase().includes(q) ||
      (m.name || '').toLowerCase().includes(q);
    const matchesGroup =
      !groupFilter.value || (m.group_keys || []).includes(groupFilter.value);
    return matchesQuery && matchesGroup;
  });
});

const allVisibleSelected = computed(
  () =>
    filteredMembers.value.length > 0 &&
    filteredMembers.value.every(m => selectedIds.value.includes(m.id))
);

const groupLabel = key => {
  const group = groups.value.find(g => g.key === key);
  return group ? group.label || group.key : key;
};

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

async function loadMembers() {
  isLoading.value = true;
  try {
    const [membersData, groupsData] = await Promise.all([
      store.dispatch('appBuilder/fetchMembers', { appId: props.appId }),
      store.dispatch('appBuilder/fetchMemberGroups', props.appId),
    ]);
    members.value = membersData.payload || [];
    groups.value = groupsData || [];
    selectedIds.value = [];
    emit('countChanged', members.value.length);
    emit('groupsChanged', groups.value);
  } catch {
    useAlert(t('APP_BUILDER.MEMBERS.ALERTS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
}

function toggleSelectAll() {
  if (allVisibleSelected.value) {
    selectedIds.value = [];
  } else {
    selectedIds.value = filteredMembers.value.map(m => m.id);
  }
}

function toggleSelect(id) {
  const idx = selectedIds.value.indexOf(id);
  if (idx === -1) selectedIds.value.push(id);
  else selectedIds.value.splice(idx, 1);
}

function openInvite() {
  formDialogRef.value?.open();
}

function openEdit(member) {
  formDialogRef.value?.open(member);
}

function openGroups() {
  groupsPanelRef.value?.open();
}

async function resendInvite(member) {
  try {
    await store.dispatch('appBuilder/resendMemberInvite', {
      appId: props.appId,
      id: member.id,
    });
    useAlert(t('APP_BUILDER.MEMBERS.ALERTS.INVITE_RESENT'));
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.SAVE_ERROR'));
  }
}

async function removeMember(member) {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      t('APP_BUILDER.MEMBERS.CONFIRM_REMOVE', {
        name: member.name || member.email,
      })
    )
  ) {
    return;
  }
  try {
    await store.dispatch('appBuilder/deleteMember', {
      appId: props.appId,
      id: member.id,
    });
    useAlert(t('APP_BUILDER.MEMBERS.ALERTS.REMOVED'));
    await loadMembers();
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.DELETE_ERROR'));
  }
}

async function bulkRemove() {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      t('APP_BUILDER.MEMBERS.CONFIRM_BULK_REMOVE', {
        count: selectedIds.value.length,
      })
    )
  ) {
    return;
  }
  try {
    await Promise.all(
      selectedIds.value.map(id =>
        store.dispatch('appBuilder/deleteMember', { appId: props.appId, id })
      )
    );
    useAlert(t('APP_BUILDER.MEMBERS.ALERTS.REMOVED'));
    await loadMembers();
  } catch {
    useAlert(t('APP_BUILDER.ALERTS.DELETE_ERROR'));
  }
}

onMounted(loadMembers);
</script>

<template>
  <div class="flex-1 overflow-y-auto p-6 bg-n-slate-2">
    <div class="max-w-5xl mx-auto flex flex-col gap-4">
      <!-- Toolbar -->
      <div class="flex flex-wrap items-center gap-2">
        <div class="relative flex-1 min-w-[200px]">
          <span
            class="i-lucide-search absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-n-slate-10"
          />
          <input
            v-model="search"
            type="search"
            :placeholder="t('APP_BUILDER.MEMBERS.SEARCH_PLACEHOLDER')"
            :aria-label="t('APP_BUILDER.MEMBERS.SEARCH_PLACEHOLDER')"
            class="w-full ps-8 pe-2.5 py-2 rounded-lg bg-n-background border border-n-weak text-sm text-n-slate-12"
          />
        </div>
        <select
          v-model="groupFilter"
          :aria-label="t('APP_BUILDER.MEMBERS.FILTER_GROUP')"
          class="px-2.5 py-2 rounded-lg bg-n-background border border-n-weak text-sm text-n-slate-12"
        >
          <option value="">
            {{ t('APP_BUILDER.MEMBERS.ALL_GROUPS') }}
          </option>
          <option v-for="group in groups" :key="group.key" :value="group.key">
            {{ group.label || group.key }}
          </option>
        </select>
        <NextButton
          :label="t('APP_BUILDER.MEMBERS.MANAGE_GROUPS')"
          icon="i-lucide-users-round"
          variant="faded"
          color="slate"
          size="sm"
          @click="openGroups"
        />
        <NextButton
          :label="t('APP_BUILDER.MEMBERS.INVITE')"
          icon="i-lucide-user-plus"
          color="blue"
          size="sm"
          @click="openInvite"
        />
      </div>

      <!-- Bulk action bar -->
      <div
        v-if="selectedIds.length"
        class="flex items-center gap-3 rounded-lg border border-n-weak bg-n-background px-3 py-2"
      >
        <span class="text-sm text-n-slate-11">
          {{ t('APP_BUILDER.MEMBERS.SELECTED', { count: selectedIds.length }) }}
        </span>
        <NextButton
          :label="t('APP_BUILDER.MEMBERS.REMOVE_SELECTED')"
          icon="i-lucide-trash-2"
          variant="ghost"
          color="ruby"
          size="xs"
          @click="bulkRemove"
        />
      </div>

      <!-- Table -->
      <div
        class="rounded-xl border border-n-weak bg-n-background overflow-hidden"
      >
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-n-weak text-n-slate-10">
                <th class="w-10 px-3 py-2.5 text-start">
                  <input
                    type="checkbox"
                    :checked="allVisibleSelected"
                    :aria-label="t('APP_BUILDER.MEMBERS.SELECT_ALL')"
                    class="align-middle"
                    @change="toggleSelectAll"
                  />
                </th>
                <th class="px-3 py-2.5 text-start font-medium">
                  {{ t('APP_BUILDER.MEMBERS.COLUMNS.NAME') }}
                </th>
                <th class="px-3 py-2.5 text-start font-medium">
                  {{ t('APP_BUILDER.MEMBERS.COLUMNS.EMAIL') }}
                </th>
                <th class="px-3 py-2.5 text-start font-medium">
                  {{ t('APP_BUILDER.MEMBERS.COLUMNS.ROLE') }}
                </th>
                <th class="px-3 py-2.5 text-start font-medium">
                  {{ t('APP_BUILDER.MEMBERS.COLUMNS.GROUPS') }}
                </th>
                <th class="px-3 py-2.5 text-start font-medium">
                  {{ t('APP_BUILDER.MEMBERS.COLUMNS.STATUS') }}
                </th>
                <th class="px-3 py-2.5 text-start font-medium">
                  {{ t('APP_BUILDER.MEMBERS.COLUMNS.LAST_SEEN') }}
                </th>
                <th class="w-px px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              <tr v-if="isLoading">
                <td colspan="8" class="px-3 py-10 text-center text-n-slate-10">
                  {{ t('APP_BUILDER.MEMBERS.LOADING') }}
                </td>
              </tr>
              <tr v-else-if="!filteredMembers.length">
                <td colspan="8" class="px-3 py-12 text-center">
                  <p class="text-sm text-n-slate-11">
                    {{
                      members.length
                        ? t('APP_BUILDER.MEMBERS.NO_MATCHES')
                        : t('APP_BUILDER.MEMBERS.EMPTY')
                    }}
                  </p>
                </td>
              </tr>
              <template v-else>
                <tr
                  v-for="member in filteredMembers"
                  :key="member.id"
                  class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1"
                >
                  <td class="px-3 py-2.5">
                    <input
                      type="checkbox"
                      :checked="selectedIds.includes(member.id)"
                      :aria-label="member.email"
                      class="align-middle"
                      @change="toggleSelect(member.id)"
                    />
                  </td>
                  <td class="px-3 py-2.5 text-n-slate-12">
                    {{ member.name || '—' }}
                  </td>
                  <td class="px-3 py-2.5 text-n-slate-11">
                    {{ member.email }}
                  </td>
                  <td class="px-3 py-2.5 text-n-slate-11">
                    {{
                      member.role === 'admin'
                        ? t('APP_BUILDER.MEMBERS.ROLE.ADMIN')
                        : t('APP_BUILDER.MEMBERS.ROLE.MEMBER')
                    }}
                  </td>
                  <td class="px-3 py-2.5">
                    <div class="flex flex-wrap gap-1">
                      <span
                        v-for="key in member.group_keys || []"
                        :key="key"
                        class="text-[11px] px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11"
                      >
                        {{ groupLabel(key) }}
                      </span>
                      <span
                        v-if="!(member.group_keys || []).length"
                        class="text-n-slate-9"
                      >
                        —
                      </span>
                    </div>
                  </td>
                  <td class="px-3 py-2.5">
                    <span
                      class="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded"
                      :class="
                        member.status === 'active'
                          ? 'text-emerald-600 bg-emerald-500/10'
                          : 'text-amber-600 bg-amber-500/10'
                      "
                    >
                      <span
                        :class="
                          member.status === 'active'
                            ? 'i-lucide-check-circle-2'
                            : 'i-lucide-clock'
                        "
                        class="w-3 h-3"
                      />
                      {{
                        member.status === 'active'
                          ? t('APP_BUILDER.MEMBERS.STATUS.ACTIVE')
                          : t('APP_BUILDER.MEMBERS.STATUS.INVITED')
                      }}
                    </span>
                  </td>
                  <td class="px-3 py-2.5 text-n-slate-10 tabular-nums">
                    {{ formatDate(member.last_sign_in_at) }}
                  </td>
                  <td class="px-3 py-2.5">
                    <div class="flex items-center gap-1 justify-end">
                      <button
                        type="button"
                        class="w-7 h-7 rounded-md inline-flex items-center justify-center text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2"
                        :title="t('APP_BUILDER.MEMBERS.ACTIONS.EDIT')"
                        :aria-label="t('APP_BUILDER.MEMBERS.ACTIONS.EDIT')"
                        @click="openEdit(member)"
                      >
                        <span class="i-lucide-pencil w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        class="w-7 h-7 rounded-md inline-flex items-center justify-center text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2"
                        :title="t('APP_BUILDER.MEMBERS.ACTIONS.RESEND')"
                        :aria-label="t('APP_BUILDER.MEMBERS.ACTIONS.RESEND')"
                        @click="resendInvite(member)"
                      >
                        <span class="i-lucide-send w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        class="w-7 h-7 rounded-md inline-flex items-center justify-center text-n-slate-10 hover:text-red-500 hover:bg-n-alpha-2"
                        :title="t('APP_BUILDER.MEMBERS.ACTIONS.REMOVE')"
                        :aria-label="t('APP_BUILDER.MEMBERS.ACTIONS.REMOVE')"
                        @click="removeMember(member)"
                      >
                        <span class="i-lucide-trash-2 w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <MemberFormDialog
      ref="formDialogRef"
      :app-id="appId"
      :member-groups="groups"
      @saved="loadMembers"
    />
    <MemberGroupsPanel
      ref="groupsPanelRef"
      :app-id="appId"
      :groups="groups"
      @changed="loadMembers"
    />
  </div>
</template>
